<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import AdminLayout from '@/components/layout/AdminLayout.vue'
import StatsCards from '@/components/cards/StatsCards.vue'
import HeadingDefault from '@/components/Heading/HeadingOne.vue'
import api from '@/services/api'
import { notificationService } from '@/services/notificationService'
import OctagonExclamationIcon from '@/icons/OctagonExclamationIcon.vue'
import OctagonCrossIcon from '@/icons/OctagonCrossIcon.vue'
import OctagonInfoIcon from '@/icons/OctagonInfoIcon.vue'
import {
  TagIcon,
  RefreshIcon,
  SigmaIcon,
  CloneIcon,
  AgentIcon,
  BanIcon,
  BookMarkIcon,
  DocsIcon,
  TimeLineIcon,
  FileSearchIcon,
  FileExportIcon,
  IndianRupeeIcon,
  ArrowLeftDoorIcon,
  RightLeftLargeIcon,
  DoNotEnterIcon,
  ClockIcon,
  FileCheckIcon,
  WonIcon,
  CircleQuestionIcon,
  // CalendarIcon,
} from '@/icons'

interface Tender {
  status: string;
  deadline?: string;
  assignedToAgent?: string | null;
  workflowStatus?: string;
  agentWorkflowStatus?: string;
}

// ── Data ───────────────────────────────────────────────────────────────────
const activeTenders       = ref<Tender[]>([])      // Unassigned active tenders
const assignedTenders     = ref<Tender[]>([])      // Assigned tenders
const allTenders          = ref<Tender[]>([])      // ALL tenders in the system
const loadingActive       = ref(false)
const loadingAssigned     = ref(false)
const loadingAll          = ref(false)

const workflowStats = ref({
  total:           0,
  active:          0,
  expired:         0,
  assignedTenders: 0,
  assignedToAgent: 0,
  inProgress:      0,
  priceReview:     0,
  criteriaReview:  0,
  submitted:       0,
  underQuery:      0,
  won:             0,
  lost:            0,
  completed:       0,
})

const rejectedTenderCount   = ref(0)
const loadingRejectedCount  = ref(false)
const expiredTenderCount    = ref(0)
const loadingExpiredCount   = ref(false)
const watchlistTenderCount  = ref(0)
const loadingWatchlistCount = ref(false)
const lastUploadDate        = ref<string | null>(null)
const loadingLastUpload     = ref(false)

// Cleanup
const cleaningUpExpired = ref(false)
const cleanupTimer      = ref<number | null>(null)
const lastCleanupTime   = ref<string | null>(null)
const autoCleanupEnabled = ref(true)

// ── Computed - Active Tenders Section ─────────────────────────────────────
// Total Tenders - ALL tenders in the system (including assigned, won, lost, etc.)
const totalTendersCount = computed(() => allTenders.value.length)

// New Tenders - only tenders with status 'new' (freshly uploaded, not processed)
const newTenderCount = ref(parseInt(localStorage.getItem('newTenderCount') || '0'))

// Updated Tenders - tenders that were updated from previous upload
const updatedTenderCount   = computed(() => allTenders.value.filter(t => t.status === 'updated').length)

// Duplicate Tenders - exact duplicates
const duplicateTenderCount = computed(() => allTenders.value.filter(t => t.status === 'duplicate').length)

// ✅ UPDATED: Total expired count from ALL tenders (both active and assigned)
// const totalExpiredCount = computed(() => {
//   const now = new Date()
//   return allTenders.value.filter(t => {
//     if (!t.deadline) return false
//     const deadline = new Date(t.deadline)
//     return deadline < now
//   }).length
// })

// ✅ UPDATED: Upcoming deadlines from ALL tenders (both active and assigned)
// const totalUpcomingDeadlines = computed(() => {
//   const now = new Date()
//   const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000)
//   return allTenders.value.filter(t => {
//     if (!t.deadline) return false
//     const deadline = new Date(t.deadline)
//     return deadline > now && deadline <= sevenDaysFromNow
//   }).length
// })

// ── Computed - Agent Pipeline Section (ONLY from assigned tenders) ─────────
const totalAssignedCount = computed(() => assignedTenders.value.length)

const assignedToAgentCount = computed(() =>
  assignedTenders.value.filter(t => t.workflowStatus === 'assigned-to-agent').length
)

const inProgressCount = computed(() =>
  assignedTenders.value.filter(t => t.workflowStatus === 'in-progress').length
)

const readyToBidCount = computed(() =>
  assignedTenders.value.filter(t => t.workflowStatus === 'ready-to-bid').length
)

const notFitCount = computed(() =>
  assignedTenders.value.filter(t => t.workflowStatus === 'not-fit').length
)

const notBiddedCount = computed(() =>
  assignedTenders.value.filter(t => t.workflowStatus === 'not-bidded').length
)

const submittedCount = computed(() =>
  assignedTenders.value.filter(t => t.workflowStatus === 'submitted').length
)

const underQueryCount = computed(() =>
  assignedTenders.value.filter(t => t.workflowStatus === 'under-query').length
)

const completedCount = computed(() =>
  assignedTenders.value.filter(t => t.workflowStatus === 'completed').length
)

// ✅ FIXED: Upcoming deadlines from assigned tenders only
// const assignedUpcomingDeadlines = computed(() => {
//   const now = new Date()
//   const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000)
//   return assignedTenders.value.filter(t => {
//     if (!t.deadline) return false
//     const deadline = new Date(t.deadline)
//     return deadline > now && deadline <= sevenDaysFromNow
//   }).length
// })

// ✅ FIXED: Expired deadlines from assigned tenders only (matches AgentTender page)
// const assignedExpiredDeadlines = computed(() => {
//   const now = new Date()
//   return assignedTenders.value.filter(t => {
//     if (!t.deadline) return false
//     const deadline = new Date(t.deadline)
//     return deadline < now
//   }).length
// })

const totalUnderReview = computed(
  () => (workflowStats.value.priceReview || 0) + (workflowStats.value.criteriaReview || 0)
)

const visibleNotificationsCount = computed(() => {
  let n = 0
  if (expiredTenderCount.value  > 0 && !loadingExpiredCount.value)   n++
  if (lastUploadDate.value          && !loadingLastUpload.value)      n++
  if (rejectedTenderCount.value > 0 && !loadingRejectedCount.value)  n++
  if (watchlistTenderCount.value > 0 && !loadingWatchlistCount.value) n++
  return n
})

// ── Helpers ────────────────────────────────────────────────────────────────
const formatDate = (dateString: string): string => {
  if (!dateString) return 'N/A'
  try {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric'
    })
  } catch { return dateString }
}

// ── Fetch ──────────────────────────────────────────────────────────────────
const fetchActiveTenders = async () => {
  try {
    loadingActive.value = true
    const r = await api.get('/api/tenders/active', { params: { _: Date.now() } })
    activeTenders.value = r.data || []
  } catch (e) { console.error(e) }
  finally { loadingActive.value = false }
}

const fetchAssignedTenders = async () => {
  try {
    loadingAssigned.value = true
    const response = await api.get('/api/tenders/assigned-to-agents')
    if (response.data.success) {
      assignedTenders.value = response.data.tenders || []
    }
  } catch (e) {
    console.error('Error fetching assigned tenders:', e)
    assignedTenders.value = []
  } finally {
    loadingAssigned.value = false
  }
}

// NEW: Fetch ALL tenders for accurate total count
const fetchAllTenders = async () => {
  try {
    loadingAll.value = true
    // Change this line — use same endpoint as TenderListview
    const r = await api.get('/api/tenders/active', { params: { _: Date.now() } })
    allTenders.value = r.data || []
  } catch (e) {
    console.error('Error fetching all tenders:', e)
    allTenders.value = []
  } finally { loadingAll.value = false }
}

const fetchWorkflowStats = async () => {
  try {
    const r = await api.get('/api/tenders/stats')
    console.log('📊 RAW API Response:', JSON.stringify(r.data, null, 2))  // 👈 Add this
    if (r.data) {
      workflowStats.value = {
        total:           r.data.totalTenders          || 0,
        active:          r.data.activeTenders         || 0,
        expired:         r.data.expiredTenders        || 0,
        assignedTenders: r.data.assignedTenders       || 0,
        assignedToAgent: r.data.assignedTenders       || 0,
        inProgress:      r.data.inProgressTenders     || 0,
        priceReview:     r.data.priceReviewTenders    || 0,
        criteriaReview:  r.data.criteriaReviewTenders || 0,
        submitted:       r.data.submittedTenders      || 0,
        underQuery:      r.data.underQueryTenders     || 0,
        won:             r.data.wonTenders            || 0,
        lost:            r.data.lostTenders           || 0,
        completed:       r.data.completedTenders      || 0,
      }
      console.log('📊 workflowStats after mapping:', workflowStats.value)
    }
  } catch (e) { console.error(e) }
}

const fetchRejectedCount = async () => {
  try {
    loadingRejectedCount.value = true
    const r = await api.get('/api/tenders/rejected/count')
    rejectedTenderCount.value =
      typeof r.data === 'number'  ? r.data :
      r.data?.total !== undefined ? r.data.total :
      r.data?.count !== undefined ? r.data.count : 0
  } catch { rejectedTenderCount.value = 0 }
  finally { loadingRejectedCount.value = false }
}

const fetchExpiredCount = async () => {
  try {
    loadingExpiredCount.value = true
    const r = await api.get('/api/tenders/expired/count')
    expiredTenderCount.value = r.data?.expiredCount ?? r.data?.totalExpiredCount ?? 0
  } catch { expiredTenderCount.value = 0 }
  finally { loadingExpiredCount.value = false }
}

const fetchWatchlistCount = async () => {
  try {
    loadingWatchlistCount.value = true
    const r = await api.get('/api/tenders/watchlist/count')
    watchlistTenderCount.value = r.data?.count ?? 0
  } catch { watchlistTenderCount.value = 0 }
  finally { loadingWatchlistCount.value = false }
}

const fetchLastUploadDate = async () => {
  try {
    loadingLastUpload.value = true
    const r = await api.get('/api/upload/last')
    if (r.data?.lastUpload) lastUploadDate.value = r.data.lastUpload
  } catch { /* silent */ }
  finally { loadingLastUpload.value = false }
}

// ── Auto-cleanup ───────────────────────────────────────────────────────────
const performAutoCleanup = async (silent = false) => {
  if (!autoCleanupEnabled.value) return
  try {
    if (lastCleanupTime.value) {
      const hrs = (Date.now() - new Date(lastCleanupTime.value).getTime()) / 3_600_000
      if (hrs < 23) return
    }
    await fetchExpiredCount()
    if (expiredTenderCount.value <= 0) return
    cleaningUpExpired.value = true
    const res = await api.post('/api/tenders/cleanup-expired')
    if (res.data.success) {
      lastCleanupTime.value = new Date().toISOString()
      localStorage.setItem('lastCleanupTime', lastCleanupTime.value)
      await Promise.all([
        fetchActiveTenders(),
        fetchAssignedTenders(),
        fetchAllTenders(),  // Also refresh all tenders
        fetchExpiredCount(),
        fetchRejectedCount(),
        fetchWorkflowStats(),
        fetchWatchlistCount()
      ])
      if (!silent) notificationService.success(`Cleaned up ${res.data.stats.activeMarkedExpired} expired tenders`, 4000)
    }
  } catch {
    if (!silent) notificationService.error('Auto cleanup failed.')
  } finally { cleaningUpExpired.value = false }
}

const initializeAutoCleanup = () => {
  const saved = localStorage.getItem('lastCleanupTime')
  if (saved) lastCleanupTime.value = saved
  performAutoCleanup(true)
  cleanupTimer.value = window.setInterval(() => performAutoCleanup(true), 24 * 60 * 60 * 1000)
}

onMounted(async () => {
  await Promise.all([
    fetchActiveTenders(),
    fetchAssignedTenders(),
    fetchAllTenders(),  // Fetch all tenders for accurate counts
    fetchWorkflowStats(),
    fetchRejectedCount(),
    fetchExpiredCount(),
    fetchWatchlistCount(),
    fetchLastUploadDate(),
  ])
  initializeAutoCleanup()
})

onUnmounted(() => { if (cleanupTimer.value) clearInterval(cleanupTimer.value) })
</script>

<template>
  <AdminLayout>
    <HeadingDefault heading="Dashboard" :icon="TagIcon" />

    <div class="space-y-8">
      <!-- ── Notification Banners ─────────────────────────────────────────── -->
      <div
        v-if="visibleNotificationsCount > 0"
        class="grid gap-4"
        :class="{
          'grid-cols-1':                               visibleNotificationsCount === 1,
          'grid-cols-1 md:grid-cols-2':                visibleNotificationsCount === 2,
          'grid-cols-1 md:grid-cols-2 lg:grid-cols-3': visibleNotificationsCount === 3,
          'grid-cols-1 md:grid-cols-2 lg:grid-cols-4': visibleNotificationsCount >= 4,
        }"
      >
        <!-- Notification banners remain the same -->
        <div v-if="expiredTenderCount > 0 && !loadingExpiredCount"
          class="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <OctagonCrossIcon class="h-8 w-8 text-red-400 shrink-0 mt-0.5" />
          <div>
            <h3 class="text-sm font-medium text-red-800">Expired Tenders</h3>
            <p class="mt-1 text-sm text-red-700">
              <span class="font-medium">{{ expiredTenderCount }}</span> tender(s) have expired.
              Auto-cleanup runs every 24 h.
            </p>
            <p v-if="lastCleanupTime" class="text-xs text-red-500 mt-0.5">
              Last cleanup: {{ new Date(lastCleanupTime).toLocaleString() }}
            </p>
          </div>
        </div>

        <div v-if="lastUploadDate && !loadingLastUpload"
          class="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
          <OctagonInfoIcon class="h-8 w-8 text-blue-400 shrink-0 mt-0.5" />
          <div>
            <h3 class="text-sm font-medium text-blue-800">Last Excel Upload</h3>
            <p class="mt-1 text-sm text-blue-700">
              Uploaded on <span class="font-medium">{{ formatDate(lastUploadDate) }}</span>
            </p>
            <router-link to="/tenders" class="text-xs text-blue-600 hover:underline mt-0.5 inline-block">
              Upload new data →
            </router-link>
          </div>
        </div>

        <div v-if="rejectedTenderCount > 0 && !loadingRejectedCount"
          class="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
          <OctagonExclamationIcon class="h-8 w-8 text-amber-400 shrink-0 mt-0.5" />
          <div>
            <h3 class="text-sm font-medium text-amber-800">Rejected Tenders</h3>
            <p class="mt-1 text-sm text-amber-700">
              <span class="font-medium">{{ rejectedTenderCount }}</span> tender(s) rejected.
            </p>
            <router-link to="/rejected-tenders" class="text-xs text-amber-700 font-medium hover:underline mt-0.5 inline-block">
              View Rejected Tenders →
            </router-link>
          </div>
        </div>

        <div v-if="watchlistTenderCount > 0 && !loadingWatchlistCount"
          class="bg-purple-50 border border-purple-200 rounded-lg p-4 flex items-start gap-3">
          <BookMarkIcon class="h-8 w-8 text-purple-400 shrink-0 mt-0.5" />
          <div>
            <h3 class="text-sm font-medium text-purple-800">Watchlist</h3>
            <p class="mt-1 text-sm text-purple-700">
              <span class="font-medium">{{ watchlistTenderCount }}</span> tender(s) in watchlist.
            </p>
            <router-link to="/watchlist-tenders" class="text-xs text-purple-700 font-medium hover:underline mt-0.5 inline-block">
              View Watchlist →
            </router-link>
          </div>
        </div>
      </div>

      <!-- ── Section: Active Tenders (All tenders in system) ───────────────── -->
      <section>
        <div class="flex items-center justify-between mb-3">
          <h2 class="text-base font-semibold text-gray-700 dark:text-gray-200">Tender Overview</h2>
          <router-link to="/tenders" class="text-xs text-blue-600 hover:underline font-medium">
            View &amp; Manage →
          </router-link>
        </div>
        <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          <StatsCards title="Unassigned Tenders"      :value="totalTendersCount"        :icon="ArrowLeftDoorIcon"     />
          <StatsCards title="New Tenders"        :value="newTenderCount"           :icon="TagIcon"       />
          <StatsCards title="Updated Tenders"    :value="updatedTenderCount"       :icon="RefreshIcon"   />
          <StatsCards title="Duplicate Tenders"  :value="duplicateTenderCount"     :icon="CloneIcon"     />
          <!-- <StatsCards title="Upcoming Deadline (All)"      :value="totalUpcomingDeadlines"   :icon="CalendarIcon"  /> -->
          <!-- <StatsCards title="Expired (All)"      :value="totalExpiredCount"        :icon="BanIcon"       /> -->
        </div>
      </section>

      <!-- ── Section: Agent Pipeline (ONLY assigned tenders) ───────────────── -->
      <section>
        <div class="flex items-center justify-between mb-3">
          <h2 class="text-base font-semibold text-gray-700 dark:text-gray-200">Agent Pipeline</h2>
          <router-link to="/agent-tenders" class="text-xs text-blue-600 hover:underline font-medium">
            View Agent Tenders →
          </router-link>
        </div>
        <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          <StatsCards title="Total Assigned Tenders"      :value="totalAssignedCount"         :icon="SigmaIcon"       />
          <router-link to="/assigned-tenders-admin" class="group block">
            <StatsCards
              title="Assigned - (Not Started)"
              :value="assignedToAgentCount"
              :icon="AgentIcon"
              class="group-hover:ring-2 group-hover:ring-blue-400 transition rounded-lg"
            />
          </router-link>
          <router-link to="/in-progress-admin" class="group block">
            <StatsCards
              title="In Progress"
              :value="inProgressCount"
              :icon="TimeLineIcon"
              class="group-hover:ring-2 group-hover:ring-blue-400 transition rounded-lg"
            />
          </router-link>
          <router-link to="/ready-to-bid-admin" class="group block">
            <StatsCards
              title="Ready to Bid"
              :value="readyToBidCount"
              :icon="RightLeftLargeIcon"
              class="group-hover:ring-2 group-hover:ring-blue-400 transition rounded-lg"
            />
          </router-link>
          <router-link to="/not-fit-admin" class="group block">
            <StatsCards
              title="Not Fit"
              :value="notFitCount"
              :icon="DoNotEnterIcon"
              class="group-hover:ring-2 group-hover:ring-blue-400 transition rounded-lg"
            />
          </router-link>
          <router-link to="/not-bidded-admin" class="group block">
            <StatsCards
              title="Not Bidded"
              :value="notBiddedCount"
              :icon="ClockIcon"
              class="group-hover:ring-2 group-hover:ring-blue-400 transition rounded-lg"
            />
          </router-link>
          <router-link to="/submitted-admin" class="group block">
            <StatsCards
              title="Submitted"
              :value="submittedCount"
              :icon="FileExportIcon"
              class="group-hover:ring-2 group-hover:ring-blue-400 transition rounded-lg"
            />
          </router-link>
          <!-- <StatsCards title="Upcoming Deadline (Agent)"       :value="assignedUpcomingDeadlines"  :icon="CalendarIcon"    /> -->
          <!-- <StatsCards title="Expired (Agent)"             :value="assignedExpiredDeadlines"   :icon="BanIcon"         /> -->
          <router-link to="/under-query-admin" class="group block">
            <StatsCards
              title="Under Query"
              :value="underQueryCount"
              :icon="CircleQuestionIcon"
              class="group-hover:ring-2 group-hover:ring-blue-400 transition rounded-lg"
            />
          </router-link>
        </div>
      </section>

      <!-- ── Section: Under Review ───────────────────────────────────────── -->
      <section>
        <div class="flex items-center justify-between mb-3">
          <h2 class="text-base font-semibold text-gray-700 dark:text-gray-200">Under Review</h2>
          <router-link to="/review-tenders" class="text-xs text-blue-600 hover:underline font-medium">
            View Review Tenders →
          </router-link>
        </div>
        <div class="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <StatsCards
            title="Total Under Review"
            :value="totalUnderReview"
            :icon="DocsIcon"
            class="group-hover:ring-2 group-hover:ring-blue-400 transition rounded-lg"
          />
          <router-link to="/price-review-admin" class="group block">
            <StatsCards
              title="Price Review"
              :value="workflowStats.priceReview"
              :icon="IndianRupeeIcon"
              class="group-hover:ring-2 group-hover:ring-blue-400 transition rounded-lg"
            />
          </router-link>
          <router-link to="/criteria-review-admin" class="group block">
            <StatsCards
              title="Criteria Review"
              :value="workflowStats.criteriaReview"
              :icon="FileSearchIcon"
              class="group-hover:ring-2 group-hover:ring-blue-400 transition rounded-lg"
            />
          </router-link>
        </div>
      </section>

      <!-- ── Section: Outcomes ──────────────────────────────────────────── -->
      <section>
        <h2 class="text-base font-semibold text-gray-700 dark:text-gray-200 mb-3">Outcomes</h2>
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <router-link to="/won-tenders" class="group block">
            <StatsCards
              title="Total Won Tenders"
              :value="workflowStats.won"
              :icon="WonIcon"
              class="group-hover:ring-2 group-hover:ring-green-400 transition rounded-lg"
            />
          </router-link>
          <router-link to="/lost-tenders" class="group block">
            <StatsCards
              title="Total Lost Tenders"
              :value="workflowStats.lost"
              :icon="BanIcon"
              class="group-hover:ring-2 group-hover:ring-red-400 transition rounded-lg"
            />
          </router-link>
          <router-link to="/completed-tenders" class="group block">
            <StatsCards
              title="Total Completed"
              :value="completedCount"
              :icon="FileCheckIcon"
              class="group-hover:ring-2 group-hover:ring-blue-400 transition rounded-lg"
            />
          </router-link>
        </div>
      </section>
    </div>
  </AdminLayout>
</template>
